import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  capacidadeDaMaquina,
  CHAVES_DE_CAPACIDADE,
  combinarModelos,
  modeloPadrao,
  MODO_PADRAO,
  MODOS_PERMISSAO,
  modoDisponivelNaMaquina,
  modoPerigoso,
  modosDoMotor,
  ROTAS,
  type IdModoPermissao,
  type ModeloDaMaquina,
  type ModeloOpencode,
} from '@remoto/shared';
import { ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';
import { chamar } from '../../api';
import { mensagemDoErro } from '../../erros';
import { usarSessao } from '../../estado/sessao';
import type { Sessao } from '../../sessoes/conversa';
import {
  catalogoDoMotor,
  esforcoAoTrocarDeMotor,
  fraseDaFaltaDeChave,
  lerCatalogoOpencode,
  motivoOpencodeIndisponivel,
  motorSaiDeCasa,
  opcoesDeEsforco,
  origemDoCatalogo,
  recursoOpencodeLigado,
  textoDaConfirmacaoOpencode,
  type MaquinaParaOpencode,
} from '../../sessoes/criacao-por-motor';
import {
  destinosDaTroca,
  primeiroDestinoAberto,
  type DestinoDaTroca,
} from '../../sessoes/motor-na-tela';
import { provedorDoCatalogo, usarCatalogo } from '../../configuracoes/dados';
import { EscolhaDeModelo } from '../../configuracoes/EscolhaDeModelo';
import { esforcoAposTrocarModelo } from '../../configuracoes/regras';
import type { RecursosDoServidor } from '../../workspace/paginas';
import { Modal } from '../Modal';
import { CaixaDeMarcar, CampoDeEscolha } from '../campos';
import { SeloForaDeCasa } from '../SeloForaDeCasa';

/**
 * Continuar a conversa NOUTRO MOTOR (D19), com os três motores (F17).
 *
 * Com dois motores o destino era "o outro", e o botão já dizia qual. Com três
 * a escolha existe — e ela é feita num seletor de DESTINO dentro do modal, e
 * não em três botões na barra do compositor: a barra já divide a largura do
 * telefone com Interromper, Encerrar, modo e modelo, e um botão por destino a
 * faria quebrar em três linhas.
 */

/** O `/auth/eu` que esta tela lê: só os recursos. */
interface Eu {
  recursos?: RecursosDoServidor;
}

/** O pedaço de `GET /maquinas` que a troca lê: a sonda, o catálogo do Codex e o retrato do OpenCode. */
interface MaquinaDaTroca extends MaquinaParaOpencode {
  id: string;
  versaoAgente?: string | null;
  codex?: { modelos?: ModeloDaMaquina[] } | null;
}

function usarEu(ativo: boolean) {
  return useQuery({
    queryKey: ['eu'],
    queryFn: () => chamar<Eu>(ROTAS.auth.eu),
    staleTime: 5 * 60_000,
    enabled: ativo,
  });
}

/**
 * Os destinos da troca vistos DA CONVERSA — a conta barata, que decide se o
 * botão existe.
 *
 * Barata porque só lê o que a conversa já tem (a sonda da máquina, o papel) e
 * o `/auth/eu`, que o painel já guardou: o retrato da chave e o 404 do catálogo
 * pedem duas consultas a mais, e ficam para o modal, que as faz ao abrir. O
 * que passa aqui e fecha lá aparece FECHADO no seletor, com o motivo — nunca
 * uma troca que esbarra na API depois de tudo escolhido.
 *
 * O botão só existe com algum destino aberto: ausência, nunca botão cinza (o
 * padrão do "continuar aqui").
 */
export function usarDestinosDaTroca(
  sessao: Sessao | undefined,
  origem: Motor | null,
  ativo: boolean,
): DestinoDaTroca[] {
  const { data: eu } = usarEu(ativo && origem !== null);
  if (!sessao || origem === null) return [];
  return destinosDaTroca(origem, {
    codex: capacidadeDaMaquina(sessao.maquina.capacidades, CHAVES_DE_CAPACIDADE.codex),
    motivoOpencode: motivoOpencodeIndisponivel(
      recursoOpencodeLigado(eu?.recursos, { estado: 'carregando' }),
      sessao.maquina,
    ),
  });
}

function rotuloDoModo(id: string | null): string {
  return MODOS_PERMISSAO.find((m) => m.id === id)?.rotulo ?? String(id);
}

/**
 * O modal da troca.
 *
 * Ele existe porque a troca tem escolhas que não podem ser caladas: o DESTINO
 * (três motores), o MODELO (obrigatório rumo ao Codex e ao OpenCode — o
 * catálogo vem da máquina ou do provedor de modelos da frota, e `sdk.capacidades` é por sessão,
 * de uma sessão que ainda não existe), o MODO (sempre escolhido: `plan` não
 * existe no Codex, `auto` e `dontAsk` não existem no OpenCode) e o esforço.
 * Rumo ao OpenCode, mais o selo e a confirmação de que a conversa sai da rede
 * de casa (D10, D11). O rodapé é FIXO: o botão que decide não rola.
 */
export function ModalDeTrocaDeMotor({
  sessao,
  origem,
  aoFechar,
  aoIrParaSessao,
}: {
  sessao: Sessao;
  origem: Motor;
  aoFechar: () => void;
  aoIrParaSessao?: ((sessaoId: string) => void) | undefined;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const papel = usarSessao((e) => e.usuario?.papel);
  const [erro, setErro] = useState('');

  const { data: maquinas = [] } = useQuery({
    queryKey: ['maquinas'],
    queryFn: () => chamar<MaquinaDaTroca[]>(ROTAS.maquinas),
  });
  const daMaquina = maquinas.find((m) => m.id === sessao.maquina.id);
  const capacidades = daMaquina?.capacidades ?? sessao.maquina.capacidades;

  /*
    O OpenCode fecha por quatro razões, e duas só se sabem aqui: a chave
    daquela máquina (o retrato do pulso, em `GET /maquinas`) e o recurso
    desligado pelo 404 do catálogo — que é também a lista de modelos, com os
    favoritos primeiro. O catálogo é o mesmo da tela de criar, guardado por
    5 min: lê-lo numa conversa do próprio OpenCode não custa nada.
  */
  const { data: eu } = usarEu(true);
  const consultaOpencode = usarCatalogo('');
  const catalogoOpencode = lerCatalogoOpencode(consultaOpencode.data, consultaOpencode.error);
  const recursoLigado = recursoOpencodeLigado(eu?.recursos, catalogoOpencode);
  // O provedor ATIVO (F2), do mesmo catálogo: é ele que o selo e a confirmação nomeiam.
  const provedorAtivo = provedorDoCatalogo(consultaOpencode.data);

  const destinos = destinosDaTroca(origem, {
    codex: capacidadeDaMaquina(capacidades, CHAVES_DE_CAPACIDADE.codex),
    motivoOpencode: motivoOpencodeIndisponivel(
      recursoLigado,
      {
        papel: sessao.maquina.papel,
        capacidades,
        opencode: daMaquina?.opencode ?? null,
        versaoAgente: daMaquina?.versaoAgente ?? null,
      },
      provedorAtivo,
    ),
  });

  const [destino, setDestino] = useState<Motor>(
    () => primeiroDestinoAberto(destinos) ?? destinos[0]?.motor ?? origem,
  );
  const fechadoAgora = destinos.find((d) => d.motor === destino)?.fechado ?? null;
  const rotuloDestino = ROTULO_DO_MOTOR[destino];

  /** O modelo do OpenCode escolhido pela BUSCA pode não estar na lista carregada. */
  const [escolhidoNoCatalogo, setEscolhidoNoCatalogo] = useState<ModeloOpencode | null>(null);
  const loteDoCatalogo = consultaOpencode.data;
  const conhecidos = useMemo(() => {
    const mapa = new Map<string, ModeloOpencode>();
    for (const m of loteDoCatalogo?.modelos ?? []) mapa.set(m.id, m);
    if (escolhidoNoCatalogo) mapa.set(escolhidoNoCatalogo.id, escolhidoNoCatalogo);
    return mapa;
  }, [loteDoCatalogo, escolhidoNoCatalogo]);

  const fontes = {
    codex: daMaquina?.codex?.modelos ?? [],
    opencode: [...conhecidos.values()],
    padraoOpencode: loteDoCatalogo?.padrao?.modelo ?? null,
  };
  const catalogoDoDestino = catalogoDoMotor(destino, fontes);
  const modelosOferecidos = combinarModelos(catalogoDoDestino, destino);
  const origemDoModelo = origemDoCatalogo(destino);
  const semCatalogo = origemDoModelo === 'da-maquina' && modelosOferecidos.length === 0;

  /*
    O modelo nasce com o padrão do destino. Do Codex ele só existe quando o
    catálogo da máquina chega — e por isso nasce vazio e o efeito abaixo o
    preenche, sem atropelar uma escolha já feita.
  */
  const [modelo, setModelo] = useState(() =>
    origemDoCatalogo(destino) === 'da-maquina' ? '' : (modeloPadrao(destino, catalogoDoDestino) ?? ''),
  );
  const [esforco, setEsforco] = useState(() =>
    esforcoAoTrocarDeMotor(destino, undefined, sessao.esforco ?? ''),
  );
  useEffect(() => {
    if (modelo || modelosOferecidos.length === 0) return;
    const padrao = modeloPadrao(destino, catalogoDoDestino) ?? modelosOferecidos[0]?.id ?? '';
    setModelo(padrao);
    const alvo = modelosOferecidos.find((m) => m.id === padrao);
    setEsforco(esforcoAoTrocarDeMotor(destino, alvo, esforco));
  }, [destino, modelo, modelosOferecidos, catalogoDoDestino, esforco]);

  const escolhidoAgora = modelosOferecidos.find((m) => m.id === modelo);
  const exigeConfirmarFable = escolhidoAgora?.exigeConfirmacao ?? false;
  const esforcos = opcoesDeEsforco(destino, escolhidoAgora);
  const saiDeCasa = motorSaiDeCasa(destino);

  const [confirmacaoFable, setConfirmacaoFable] = useState(false);
  const [confirmacaoOpencode, setConfirmacaoOpencode] = useState(false);

  /*
    O modo é sempre ESCOLHIDO. Pré-preenchido com o da sessão QUANDO ele
    existe no destino; senão o padrão da casa, com o aviso visível logo
    abaixo — trocar calado o modo de alguém é o que este bloco evita.
  */
  const papelDaMaquina = sessao.maquina.papel === 'VPS' ? ('VPS' as const) : ('PC' as const);
  const serveNo = (alvo: Motor, m: IdModoPermissao): boolean =>
    modosDoMotor(alvo).includes(m) &&
    modoDisponivelNaMaquina(m, alvo, papelDaMaquina, sessao.maquina.bypassTotal === true);
  const modoOriginal = MODOS_PERMISSAO.find((m) => m.id === sessao.modo)?.id ?? null;
  const modoInicial = (alvo: Motor): IdModoPermissao =>
    modoOriginal !== null && serveNo(alvo, modoOriginal) ? modoOriginal : MODO_PADRAO;
  const [modo, setModo] = useState<IdModoPermissao>(() => modoInicial(destino));
  const [confirmacaoModoPerigoso, setConfirmacaoModoPerigoso] = useState(false);
  const originalServe = modoOriginal !== null && serveNo(destino, modoOriginal);

  /** Trocar o destino troca o que faz sentido junto: modelo, esforço, modo e confirmações. */
  function escolherDestino(novo: Motor): void {
    setDestino(novo);
    setErro('');
    setConfirmacaoFable(false);
    setConfirmacaoOpencode(false);
    setConfirmacaoModoPerigoso(false);
    setEscolhidoNoCatalogo(null);
    const catalogo = catalogoDoMotor(novo, fontes);
    const padrao = modeloPadrao(novo, catalogo) ?? '';
    setModelo(padrao);
    const alvo = combinarModelos(catalogo, novo).find((m) => m.id === padrao);
    setEsforco(esforcoAoTrocarDeMotor(novo, alvo, esforco));
    setModo(modoInicial(novo));
  }

  const trocar = useMutation({
    mutationFn: () =>
      chamar<{ id: string; antigaId: string }>(`${ROTAS.sessoes}/${sessao.id}/trocar-motor`, {
        method: 'POST',
        corpo: {
          motor: destino,
          modo,
          ...(modelo ? { modelo } : {}),
          ...(esforco ? { esforco } : {}),
          confirmacaoFable,
          confirmacaoModoPerigoso,
          confirmacaoOpencode,
        },
      }),
    onSuccess: (nova) => {
      void cliente.invalidateQueries({ queryKey: ['sessoes'] });
      aoFechar();
      aoIrParaSessao?.(nova.id);
    },
    // Pelo CÓDIGO: as recusas do OpenCode (sem chave, recurso desligado, VPS,
    // confirmação) já moram no catálogo de erros, com as frases do shared.
    onError: (e) =>
      setErro(
        mensagemDoErro(e, 'Não foi possível trocar o motor desta conversa.', {
          mesmo_motor: 'A conversa já está neste motor — não há o que trocar.',
          sessao_com_pendencia: 'Decida o cartão pendente antes de trocar de motor.',
          codex_indisponivel: 'O Codex não está disponível nesta máquina agora.',
          modelo_obrigatorio_no_motor: `Escolha um modelo para continuar no ${rotuloDestino}.`,
          // Com o NOME do provedor ativo (F2): a tela de Configurações tem duas chaves.
          opencode_sem_chave: fraseDaFaltaDeChave(provedorAtivo),
        }),
      ),
  });

  const podeConfirmar =
    !trocar.isPending &&
    fechadoAgora === null &&
    !semCatalogo &&
    // Sem modelo não se troca rumo ao Codex e ao OpenCode: a regra da casa
    // manda modelo explícito, e lá não há política em que cair.
    (origemDoModelo === 'da-casa' || modelo.length > 0) &&
    // A confirmação do OpenCode (D10) é da pessoa: a API recusa sem ela, e o
    // botão não finge que dá.
    (!saiDeCasa || confirmacaoOpencode);

  return (
    <Modal
      titulo="Continuar em outro motor"
      aberto
      aoFechar={aoFechar}
      rodape={
        <>
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => trocar.mutate()}
            disabled={!podeConfirmar}
            className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-sky-500 disabled:opacity-50"
          >
            {trocar.isPending ? 'trocando…' : 'Continuar no ' + rotuloDestino}
          </button>
        </>
      }
    >
      {/* O rodapé decide; o que rola é só o conteúdo (teto em vh, regra da casa). */}
      <div className="max-h-[55vh] space-y-3 overflow-y-auto" data-teste="troca-de-motor">
        <p>
          Esta conversa adormece e sai da lista (fica arquivada). Uma nova nasce no {rotuloDestino},
          nesta mesma pasta, com as últimas mensagens desta como primeira mensagem. Retomar e
          desarquivar desfazem.
        </p>

        <div className="space-y-2">
          <CampoDeEscolha<Motor>
            rotulo="Motor de destino"
            valor={destino}
            opcoes={destinos.map((d) => ({
              id: d.motor,
              rotulo: ROTULO_DO_MOTOR[d.motor],
              // Fechado NÃO some: o motivo na linha ensina o gesto que abre.
              ...(d.fechado ? { desabilitada: true, ...d.fechado } : {}),
            }))}
            aoEscolher={escolherDestino}
          />
          {saiDeCasa ? <SeloForaDeCasa provedor={provedorAtivo} /> : null}
          {fechadoAgora ? (
            <p
              className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-amber-200"
              data-teste="destino-fechado"
            >
              {rotuloDestino} indisponível aqui: {fechadoAgora.motivo}
            </p>
          ) : null}
        </div>

        {semCatalogo ? (
          <p className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-amber-200">
            Esta máquina ainda não reportou os modelos do {rotuloDestino}. Sem a lista não dá para
            escolher um — e inventar um nome faria a sessão nascer quebrada. Tente de novo em um
            minuto, ou confira o motor na tela de Saúde.
          </p>
        ) : origemDoModelo === 'do-provedor' ? (
          /*
            O catálogo do provedor tem centenas de modelos (D12): o campo é o
            da tela de Configurações, com a busca indo ao servidor e os
            favoritos primeiro. O esforço sobrevive à troca de modelo só quando
            o novo o tem — senão volta ao padrão do provedor.
          */
          <EscolhaDeModelo
            rotulo="Modelo"
            valor={modelo || null}
            conhecidos={conhecidos}
            aoEscolher={(escolhido) => {
              setModelo(escolhido.id);
              setEscolhidoNoCatalogo(escolhido);
              setEsforco(esforcoAposTrocarModelo(esforco, escolhido));
            }}
          />
        ) : (
          <CampoDeEscolha
            rotulo="Modelo"
            valor={modelo || null}
            opcoes={modelosOferecidos.map((m) => ({
              id: m.id,
              rotulo: m.rotulo,
              perigoso: m.exigeConfirmacao,
            }))}
            aoEscolher={(id) => {
              setModelo(id);
              setConfirmacaoFable(false);
              const alvo = modelosOferecidos.find((m) => m.id === id);
              if (alvo?.esforcoPadrao) setEsforco(alvo.esforcoPadrao);
            }}
          />
        )}

        <CampoDeEscolha<IdModoPermissao>
          rotulo="Como pedir permissão"
          valor={modo}
          opcoes={MODOS_PERMISSAO.filter((m) => serveNo(destino, m.id)).map((m) => ({
            id: m.id,
            rotulo: m.rotulo,
            perigoso: m.perigoso,
            ...(m.perigoso && papel !== 'ADMIN'
              ? {
                  desabilitada: true,
                  descricao: 'só ADMIN',
                  motivo: 'modos que dispensam aprovação exigem papel ADMIN',
                }
              : {}),
          }))}
          aoEscolher={(id) => {
            setModo(id);
            setConfirmacaoModoPerigoso(false);
          }}
        />
        {!originalServe && modoOriginal ? (
          <p className="text-xs text-amber-300">
            O modo «{rotuloDoModo(modoOriginal)}» não existe no {rotuloDestino} nesta máquina; esta
            conversa seguirá em «{rotuloDoModo(modo)}».
          </p>
        ) : null}

        {esforcos.length > 1 ? (
          <CampoDeEscolha
            rotulo="Esforço de raciocínio"
            valor={esforco}
            opcoes={esforcos}
            aoEscolher={setEsforco}
          />
        ) : (
          <p className="text-xs text-zinc-500" data-teste="sem-esforco">
            Este modelo não tem níveis de esforço: vale o padrão do provedor.
          </p>
        )}

        {modoPerigoso(modo) && papel === 'ADMIN' ? (
          <CaixaDeMarcar
            marcada={confirmacaoModoPerigoso}
            aoMarcar={setConfirmacaoModoPerigoso}
            className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-amber-200"
          >
            Entendo que neste modo a sessão age sem pedir aprovação, e assumo o risco.
          </CaixaDeMarcar>
        ) : null}
        {exigeConfirmarFable ? (
          <CaixaDeMarcar
            marcada={confirmacaoFable}
            aoMarcar={setConfirmacaoFable}
            className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-amber-200"
          >
            Entendo que este modelo cobra crédito extra sem perguntar quando a cota acaba.
          </CaixaDeMarcar>
        ) : null}
        {saiDeCasa ? (
          <CaixaDeMarcar
            marcada={confirmacaoOpencode}
            aoMarcar={setConfirmacaoOpencode}
            className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-amber-200"
          >
            {textoDaConfirmacaoOpencode(provedorAtivo)}
          </CaixaDeMarcar>
        ) : null}

        {erro ? <p className="text-sm text-red-300">{erro}</p> : null}
      </div>
    </Modal>
  );
}
